import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import PatientNavigationMenu from '../../components/ui/PatientNavigationMenu';
import GameCard from './components/GameCard';
import ReactionTimeGame from './components/ReactionTimeGame';
import GameHistory from './components/GameHistory';
import GameInstructions from './components/GameInstructions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CognitiveGamesSection = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('games');
  const navigate = useNavigate();

  // Mock user data
  const mockUser = {
    id: 'patient_001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    role: 'patient',
    joinedAt: '2024-01-15'
  };

  // Available cognitive games
  const availableGames = [
    {
      id: 'reaction-time-1',
      name: 'Reaction Time Test',
      type: 'reaction',
      difficulty: 'easy',
      description: 'Test your reaction speed by responding to visual stimuli as quickly as possible. Measures response time in milliseconds.',
      icon: 'Zap',
      duration: '2-3 min',
      attempts: 5,
      personalBest: '245ms'
    },
    {
      id: 'memory-sequence-1',
      name: 'Memory Sequence',
      type: 'memory',
      difficulty: 'medium',
      description: 'Remember and repeat increasingly complex sequences of colors and patterns to test your working memory.',
      icon: 'Brain',
      duration: '5-10 min',
      attempts: 10,
      personalBest: '8 correct'
    },
    {
      id: 'attention-focus-1',
      name: 'Focus Challenge',
      type: 'attention',
      difficulty: 'hard',
      description: 'Maintain focus on specific targets while ignoring distractions. Tests sustained attention and concentration.',
      icon: 'Eye',
      duration: '3-5 min',
      attempts: 1,
      personalBest: '85% accuracy'
    },
    {
      id: 'processing-speed-1',
      name: 'Processing Speed',
      type: 'processing',
      difficulty: 'medium',
      description: 'Complete simple tasks as quickly and accurately as possible to measure cognitive processing speed.',
      icon: 'Gauge',
      duration: '4-6 min',
      attempts: 3,
      personalBest: null
    }
  ];

  useEffect(() => {
    // Simulate user authentication check
    const user = localStorage.getItem('currentUser');
    if (user) {
      setCurrentUser(JSON.parse(user));
    } else {
      setCurrentUser(mockUser);
      localStorage.setItem('currentUser', JSON.stringify(mockUser));
    }

    // Load game history from localStorage
    const savedHistory = localStorage.getItem('gameHistory');
    if (savedHistory) {
      setGameHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleStartGame = (game) => {
    setSelectedGame(game);
    if (game?.type === 'reaction') {
      setActiveGame('reaction-time');
    } else {
      // For other game types, show instructions first
      setShowInstructions(true);
    }
  };

  const handleShowInstructions = (game) => {
    setSelectedGame(game);
    setShowInstructions(true);
  };

  const handleGameComplete = (gameResult) => {
    // Save game result to history
    const newGameEntry = {
      id: Date.now(),
      gameType: gameResult?.gameType,
      gameName: selectedGame?.name || 'Cognitive Game',
      score: gameResult?.averageReactionTime || gameResult?.score,
      unit: gameResult?.gameType === 'reaction-time' ? 'ms' : 'points',
      completedAt: gameResult?.completedAt,
      rating: getRatingFromScore(gameResult?.averageReactionTime || gameResult?.score, gameResult?.gameType),
      attempts: gameResult?.attempts?.length || 1,
      details: gameResult
    };

    const updatedHistory = [newGameEntry, ...gameHistory];
    setGameHistory(updatedHistory);
    localStorage.setItem('gameHistory', JSON.stringify(updatedHistory));

    // Exit game
    setActiveGame(null);
    setSelectedGame(null);
  };

  const getRatingFromScore = (score, gameType) => {
    if (gameType === 'reaction-time') {
      if (score < 200) return 'Excellent';
      if (score < 300) return 'Good';
      if (score < 400) return 'Average';
      return 'Needs Practice';
    }
    return 'Good';
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    navigate('/login-screen');
  };

  const handleProfileClick = () => {
    // Navigate to profile settings
    console.log('Profile clicked');
  };

  const handleExitGame = () => {
    setActiveGame(null);
    setSelectedGame(null);
  };

  const handleSymptomLogClick = () => {
    console.log('Symptom log modal opened');
  };

  // If a game is active, render the game component
  if (activeGame === 'reaction-time') {
    return (
      <ReactionTimeGame
        onGameComplete={handleGameComplete}
        onExit={handleExitGame}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <RoleBasedHeader
        userRole={currentUser?.role || 'patient'}
        userName={currentUser?.name || 'Patient'}
        onLogout={handleLogout}
        onProfileClick={handleProfileClick}
      />
      {/* Main Content */}
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Gamepad2" size={24} color="var(--color-primary)" />
                </div>
                <h1 className="text-3xl font-bold text-foreground">Cognitive Games</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Assess and improve your cognitive performance through scientifically-designed games and exercises. 
                Track your progress over time and monitor your neurological health.
              </p>
            </div>

            {/* Navigation Menu */}
            <PatientNavigationMenu
              onSymptomLogClick={handleSymptomLogClick}
              className="lg:ml-8"
            />
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-1 bg-card border border-border rounded-lg p-1 mb-8 w-fit">
            <button
              onClick={() => setActiveTab('games')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                activeTab === 'games' ?'bg-primary text-primary-foreground shadow-soft' :'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="Gamepad2" size={16} className="mr-2" />
              Available Games
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                activeTab === 'history' ?'bg-primary text-primary-foreground shadow-soft' :'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="BarChart3" size={16} className="mr-2" />
              Performance History
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'games' && (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Target" size={32} color="var(--color-primary)" className="mx-auto mb-3" />
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {gameHistory?.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Games Completed</div>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Calendar" size={32} color="var(--color-accent)" className="mx-auto mb-3" />
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {gameHistory?.length > 0 ? 
                      Math.ceil((Date.now() - new Date(gameHistory[gameHistory.length - 1].completedAt)) / (1000 * 60 * 60 * 24)) : 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Days Since Last Game</div>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Trophy" size={32} color="var(--color-success)" className="mx-auto mb-3" />
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {gameHistory?.filter(game => game?.rating === 'Excellent')?.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Excellent Scores</div>
                </div>
              </div>

              {/* Available Games Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableGames?.map((game) => (
                  <div key={game?.id} className="relative">
                    <GameCard
                      game={game}
                      onStartGame={handleStartGame}
                    />
                    
                    {/* Instructions Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleShowInstructions(game)}
                      className="absolute top-4 right-4"
                      iconName="HelpCircle"
                    />
                  </div>
                ))}
              </div>

              {/* Getting Started Section */}
              <div className="mt-12 bg-gradient-to-r from-primary/5 to-accent/5 border border-border rounded-lg p-8">
                <div className="max-w-3xl">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Getting Started with Cognitive Games</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium text-foreground mb-2">Why Play Cognitive Games?</h3>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Monitor cognitive performance over time</li>
                        <li>• Provide data for healthcare providers</li>
                        <li>• Track neurological health trends</li>
                        <li>• Identify changes in cognitive function</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-2">Best Practices</h3>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Play in a quiet, distraction-free environment</li>
                        <li>• Complete games at consistent times</li>
                        <li>• Take breaks between different games</li>
                        <li>• Review your progress regularly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'history' && (
            <GameHistory gameHistory={gameHistory} />
          )}
        </div>
      </div>
      {/* Game Instructions Modal */}
      {showInstructions && (
        <GameInstructions
          selectedGame={selectedGame}
          onClose={() => setShowInstructions(false)}
          onStartGame={handleStartGame}
        />
      )}
    </div>
  );
};

export default CognitiveGamesSection;